import * as UI from "@iyk/ui"
import { FormState, useForm } from "@iyk/vf-forms"
import { Analytics, useAnalytics } from "@iyk/vf-observability/react"
import React from "react"
import { Form } from "react-router"
import { appConfig } from "../../../app-config.ts"
import { GlobalRoutes } from "../../../react-router/routes.ts"
import { formatPrice } from "../../ui/format-price.ts"
import { Link } from "../../ui/link.tsx"
import type { Shop } from "../shop.ts"

export const CartSummary = ({ cart }: { cart: Shop.Cart | null }) => {
  const analytics = useAnalytics()
  const cartIsEmpty = cart?.lines.length === 0 || cart === null
  return (
    <div className="flex flex-col flex-1 h-full">
      {cartIsEmpty ? <EmptyCart /> : <CartList cart={cart} />}
      <div className="pt-3 pb-4 bshadow-t bshadow-gray-6">
        <div className="flex flex-col gap-2 text-sm text-gray-11">
          <div className="flex justify-between">
            <span>Subtotal:</span>
            <span>{formatPrice(cart?.subtotal ?? 0)}</span>
          </div>
          <div className="flex justify-between">
            <span>Shipping:</span>
            <span>Calculated at next step</span>
          </div>
        </div>
        <Form
          method="post"
          action={GlobalRoutes.shop.checkout}
          onSubmit={() => analytics.track(Analytics.Event.CheckoutStarted())}
        >
          <button
            type="submit"
            className={UI.classForButton({ size: "md", kind: "solid", className: "mt-3 w-full" })}
            disabled={cartIsEmpty}
          >
            Checkout
          </button>
        </Form>
        <p className="text-xs text-gray-11 mt-4 leading-tight">
          By checking out, you agree to the
          <br />
          {[
            {
              text: "Terms and Conditions",
              url: appConfig.assets.documents.termsOfService
            },
            { text: "Privacy Policy", url: appConfig.assets.documents.privacyPolicy }
          ].map(({ text, url }, index) => (
            <React.Fragment key={text}>
              <a href={url} className="underline underline-offset-4" target="_blank" rel="noreferrer">
                {text}
              </a>
              {index === 0 && " and "}
            </React.Fragment>
          ))}
        </p>
      </div>
    </div>
  )
}

const CartList = ({ cart }: { cart: Shop.Cart }) => {
  return (
    <div className="flex-1 overflow-auto hide-scrollbar">
      {cart.lines.map((line) => (
        <CartItem key={line.productVariant.id} line={line} />
      ))}
    </div>
  )
}

const CartItem = ({ line }: { line: Shop.Line }) => {
  return (
    <div
      key={line.productVariant.id}
      className="grid grid-cols-[auto_1fr] gap-4 bshadow-t bshadow-gray-6 py-3 h-max"
    >
      <div className="aspect-square w-[130px] bg-[rgba(66,66,66,0.20)] overflow-hidden">
        <img src={line.product.imageUrl} alt={line.product.title} className="size-full object-contain" />
      </div>
      <div className="flex flex-col justify-between h-full">
        <div>
          <p className="text-sm line-clamp-2 break-all">
            {line.product.title}{" "}
            {line.productVariant.title && <span className="text-gray-11">({line.productVariant.title})</span>}
          </p>
          <p className="text-sm">{formatPrice(line.productVariant.price)}</p>
        </div>
        <div className="flex items-center justify-between mt-2">
          <CartItemQuantitySelector line={line} />
          <CartItemRemoveButton line={line} />
        </div>
      </div>
    </div>
  )
}

const CartItemQuantitySelector = ({ line }: { line: Shop.Line }) => {
  const analytics = useAnalytics()

  const { Form } = useForm()

  const [quantity, setQuantity] = React.useState(line.quantity)

  const handleQuantityChange = (value: number) => {
    setQuantity((prev) => {
      const newQuantity = prev + value
      return newQuantity < 1 ? 1 : newQuantity
    })
  }

  return (
    <Form
      className="flex items-center gap-3"
      method="post"
      action={GlobalRoutes.shop.cartApi}
      onSubmit={() =>
        analytics.track(
          Analytics.Event.LineQuantityUpdated({
            productVariantId: line.productVariant.id,
            quantity
          })
        )
      }
    >
      <input type="hidden" name="productVariantId" value={line.productVariant.id} />
      <input type="hidden" name="quantity" value={quantity} />
      <input type="hidden" name="_tag" value="UpdateLine" />

      <button
        type="submit"
        className={UI.cx("h-5 w-4 text-sm", quantity === 1 && "opacity-50 cursor-not-allowed")}
        onClick={() => handleQuantityChange(-1)}
      >
        —
      </button>

      <span className="text-sm size-5 text-center">{quantity}</span>

      <button type="submit" className="h-5 w-4 text-sm" onClick={() => handleQuantityChange(1)}>
        +
      </button>
    </Form>
  )
}

const CartItemRemoveButton = ({ line }: { line: Shop.Line }) => {
  const analytics = useAnalytics()

  const { Form, state } = useForm()

  const isSubmitting = FormState.$is("Submitting")(state)

  return (
    <Form
      method="post"
      action={GlobalRoutes.shop.cartApi}
      onSubmit={() =>
        analytics.track(Analytics.Event.LineRemovedFromCart({ productVariantId: line.productVariant.id }))
      }
    >
      <input type="hidden" name="productVariantId" value={line.productVariant.id} />
      <button
        type="submit"
        name="_tag"
        value="RemoveLine"
        className={UI.classForLink()}
        disabled={isSubmitting}
      >
        Remove
      </button>
    </Form>
  )
}

const EmptyCart = () => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4">
      <p className="text-xl-title text-gray-11">Your cart is empty</p>
      <Link to={GlobalRoutes.shop.landing} className={UI.classForButton({ size: "md", kind: "solid" })}>
        Go to shop
      </Link>
    </div>
  )
}

namespace CartSummary {
  export interface Props {
    cart: Shop.Cart | null
  }
}
